/**
 * The blueprint's geometry, like Unreal Engine's Blueprints: each box has an input on its left
 * (where wires arrive) and its pins on its right, one per row (`boxes.ts` measures them); a
 * link leaving from a pin is a wire, a curve from that pin to the input of the box it leads to.
 * A link with no pin leaves from the right of the box's header. Pure, in world units.
 */
import type { Link } from "@protocol/Link";
import type { Pin } from "@protocol/Pin";
import type { DiagramBox, DiagramEdge, DiagramLayout } from "./layout";
import { PIN_LINE, PLUS_INSET, WIRE_STEPS } from "./constants";

/** A wire: a cubic curve from `(x1, y1)` to `(x2, y2)` through two control points. */
export interface Wire {
  /** The arrow of the layout it draws. */
  edge: DiagramEdge;
  x1: number;
  y1: number;
  c1x: number;
  c1y: number;
  c2x: number;
  c2y: number;
  x2: number;
  y2: number;
}

/** What a blueprint has under a point besides the class diagram's things. */
export type PinHit = { type: "pin"; box: DiagramBox; pin: Pin; index: number } | { type: "plus"; box: DiagramBox };

/** Where pin number `index` of `box` is: on its right border, in the middle of its row. */
export function pinPoint(box: DiagramBox, index: number): { x: number; y: number } {
  return { x: box.x + box.w, y: box.y + box.pinsTop + index * PIN_LINE + PIN_LINE / 2 };
}

/** Where wires arrive at `box`: on its left border, half-way down its header. */
export function inputPoint(box: DiagramBox): { x: number; y: number } {
  return { x: box.x, y: box.y + box.header / 2 };
}

/** Where a link leaves `box` when it has no pin (or a pin the box no longer has): the right of its header. */
function outputPoint(box: DiagramBox): { x: number; y: number } {
  return { x: box.x + box.w, y: box.y + box.header / 2 };
}

/** Where the "+" that adds a pin to `box` is: inside its header's right corner. */
export function plusPoint(box: DiagramBox): { x: number; y: number } {
  return { x: box.x + box.w - PLUS_INSET, y: box.y + PLUS_INSET };
}

/** The curve from `(x1, y1)`, leaving to the right, to `(x2, y2)`, arriving from the left. */
export function curve(x1: number, y1: number, x2: number, y2: number): Omit<Wire, "edge"> {
  // Going back (to a box on the left) bends wider, so the wire loops round instead of crossing the boxes.
  const bend = Math.max(40, Math.abs(x2 - x1) / 2, x2 < x1 ? Math.min(160, Math.abs(y2 - y1) / 2 + 60) : 0);
  return { x1, y1, c1x: x1 + bend, c1y: y1, c2x: x2 - bend, c2y: y2, x2, y2 };
}

/** The wire of `edge` from box `from` to box `to`. */
export function wireOf(edge: DiagramEdge, from: DiagramBox, to: DiagramBox): Wire {
  const index = edge.link.pin ? from.pins.findIndex((p) => p.id === edge.link.pin) : -1;
  const start = index >= 0 ? pinPoint(from, index) : outputPoint(from);
  const end = inputPoint(to);
  return { edge, ...curve(start.x, start.y, end.x, end.y) };
}

/** Every wire of the layout (one per arrow shown). */
export function wiresOf(layout: DiagramLayout): Wire[] {
  return layout.edges.flatMap((edge) => {
    const from = layout.byId.get(edge.link.from);
    const to = layout.byId.get(edge.link.to);
    return from && to ? [wireOf(edge, from, to)] : [];
  });
}

/** The point at `t` (0 to 1) along a curve. */
export function pointOn(w: Omit<Wire, "edge">, t: number): { x: number; y: number } {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return { x: a * w.x1 + b * w.c1x + c * w.c2x + d * w.x2, y: a * w.y1 + b * w.c1y + c * w.c2y + d * w.y2 };
}

/** The distance from `(x, y)` to a curve (measured along straight pieces of it). */
export function wireDistance(x: number, y: number, w: Omit<Wire, "edge">): number {
  let best = Infinity;
  let last = pointOn(w, 0);
  for (let i = 1; i <= WIRE_STEPS; i++) {
    const next = pointOn(w, i / WIRE_STEPS);
    best = Math.min(best, segment(x, y, last.x, last.y, next.x, next.y));
    last = next;
  }
  return best;
}

/** The distance from `(x, y)` to the segment from `(ax, ay)` to `(bx, by)`. */
function segment(x: number, y: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const length = dx * dx + dy * dy;
  const t = length ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / length)) : 0;
  return Math.hypot(x - (ax + t * dx), y - (ay + t * dy));
}

/**
 * The pin or "+" at `(x, y)`, the top box first, within `radius` (world units). The "+" only
 * counts when `adding` (the user may edit).
 */
export function pinHitAt(layout: DiagramLayout, x: number, y: number, radius: number, adding: boolean): PinHit | null {
  for (let i = layout.boxes.length - 1; i >= 0; i--) {
    const box = layout.boxes[i]!;
    if (x < box.x - radius || x > box.x + box.w + radius || y < box.y - radius || y > box.y + box.h + radius) continue;
    for (let index = 0; index < box.pins.length; index++) {
      const p = pinPoint(box, index);
      // The whole row counts near the pin, so a pin is easy to catch.
      if (Math.hypot(x - p.x, y - p.y) <= radius || (x >= box.x + box.w / 2 && x <= p.x && Math.abs(y - p.y) <= PIN_LINE / 2)) return { type: "pin", box, pin: box.pins[index]!, index };
    }
    if (adding) {
      const plus = plusPoint(box);
      if (Math.hypot(x - plus.x, y - plus.y) <= Math.max(radius, 8)) return { type: "plus", box };
    }
  }
  return null;
}

/** The id for a new pin of a node that has `pins`: `p1`, `p2`... the first one free. */
export function nextPinId(pins: readonly Pin[]): string {
  const taken = new Set(pins.map((p) => p.id));
  let n = 1;
  while (taken.has(`p${n}`)) n++;
  return `p${n}`;
}

/** The wire leaving pin `pin` of node `from` (one pin holds one wire), if any. */
export function wireFrom(links: readonly Link[], from: string, pin: string): Link | undefined {
  return links.find((l) => l.from === from && l.pin === pin);
}
